<script setup lang="ts">
import HasPermission from '@/components/HasPermission.vue'
import useAuthUser from '@/composables/useAuthUser'
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
import type { Company } from '@/modules/companies/interfaces/company'
import CompanyRoleAbilitiesPanel from '@/modules/companies/components/CompanyRoleAbilitiesPanel.vue'
import CreateRoleModal from '@/modules/roles/components/CreateRoleModal.vue'
import UnsavedRoleChangesModal from '@/modules/roles/components/UnsavedRoleChangesModal.vue'
import useRole from '@/modules/roles/composables/useRole'
import useRoles, { type Role } from '@/modules/roles/composables/useRoles'
import useRoleSwitchGuard from '@/modules/roles/composables/useRoleSwitchGuard'
import { provide, ref, watch } from 'vue'

const { user } = useAuthUser()
const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser()

watch(abilities, () => { redirectTo404('view-roles', 'App\\Models\\Company') }, { immediate: true })

const { companies, setQuery } = useCompaniesSearch({ immediate: true, params: {} })
const { data, isLoading, setCompanyId, refetch } = useRoles({ immediate: false })
const { deleteRoleMutationAsync } = useRole({ id: 0, immediate: false })

watch(user, (u) => {
  if (u && !u.internal && u.companyId) setCompanyId(u.companyId)
}, { immediate: true })

const selectedCompany = ref<Company | undefined>()
const companySearch = ref('')
const showCompanyDropdown = ref(false)
const showCreateModal = ref(false)
const deletingId = ref<number | null>(null)

// ── Guardado OBLIGATORIO al cambiar de rol/empresa ──────────────────────────
// Todo el flujo (selectedRole, mobileView, modal, guardado con propagación de
// error) vive en el composable compartido con RolesTab de la empresa.
const {
  selectedRole,
  mobileView,
  abilitiesPanelRef,
  showUnsavedModal,
  savingBeforeSwitch,
  saveFailed,
  runGuarded,
  applyRoleSwitch,
  selectRole,
  backToList,
  saveAndContinue,
  discardAndContinue,
  keepEditing,
} = useRoleSwitchGuard()

function handleSelectCompany(company: Company) {
  showCompanyDropdown.value = false
  // Cambiar de empresa también descarta el editor del rol actual: pasa por el
  // mismo guard para no perder cambios sin guardar en silencio.
  runGuarded(() => {
    selectedCompany.value = company
    companySearch.value = company.name ?? ''
    setCompanyId(company.id)
    applyRoleSwitch(null)
  })
}

function handleSearchInput(val: string) {
  setQuery(val)
  showCompanyDropdown.value = true
}

async function handleDelete(role: Role) {
  deletingId.value = role.id
  try {
    await deleteRoleMutationAsync(role.id)
    if (selectedRole.value?.id === role.id) selectedRole.value = null
    await refetch()
  } finally {
    deletingId.value = null
  }
}

const invalidateQueryData = async () => { await refetch() }
provide('invalidateQueryData', { invalidateQueryData })

const COMPANY = 'App\\Models\\Company'

const ROLE_COLORS: Record<string, string> = {
  Master: 'bg-purple-100 text-purple-700',
  'Travel Manager': 'bg-blue-100 text-blue-700',
  Supervisor: 'bg-amber-100 text-amber-700',
  Traveler: 'bg-emerald-100 text-emerald-700',
}
const roleColor = (name: string) => ROLE_COLORS[name] ?? 'bg-[#021F58]/10 text-[#021F58]'

function onSearchBlur() {
  setTimeout(() => { showCompanyDropdown.value = false }, 150)
}
</script>

<template>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Company selector (internal) -->
    <div v-if="user?.internal" class="mb-5 bg-white rounded-xl border border-gray-200 p-4">
      <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Empresa</p>
      <div class="relative">
        <div class="relative">
          <span class="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <span class="material-symbols-outlined text-[18px] text-gray-400">search</span>
          </span>
          <input
            v-model="companySearch"
            type="text"
            placeholder="Buscar empresa..."
            class="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 focus:border-[#021F58] placeholder-gray-400"
            @input="handleSearchInput(companySearch)"
            @focus="showCompanyDropdown = true"
            @blur="onSearchBlur"
          />
        </div>
        <div
          v-if="showCompanyDropdown && companies.length > 0"
          class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-auto"
        >
          <button
            v-for="company in companies"
            :key="company.id"
            type="button"
            class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
            @mousedown.prevent="handleSelectCompany(company)"
          >
            {{ company.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main card -->
    <div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

      <!-- Header -->
      <div class="px-4 md:px-6 py-4 md:py-5 border-b border-gray-100 flex items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <button
            v-if="mobileView === 'abilities' && selectedRole"
            type="button"
            class="md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            @click="backToList"
          >
            <span class="material-symbols-outlined text-xl leading-none">arrow_back</span>
          </button>
          <div>
            <h2 class="text-base font-semibold text-gray-900">
              <span v-if="mobileView === 'abilities' && selectedRole" class="md:hidden">{{ selectedRole.name }}</span>
              <span v-else>Roles</span>
            </h2>
            <p class="text-xs text-gray-400 mt-0.5 hidden md:block">Selecciona un rol para gestionar sus permisos</p>
          </div>
        </div>
        <HasPermission name-permission="create-roles" :entity-type="COMPANY">
          <button
            v-if="mobileView !== 'abilities'"
            type="button"
            class="inline-flex items-center gap-1.5 px-3 md:px-3.5 py-2 rounded-lg bg-[#021F58] text-white text-sm font-medium hover:bg-[#021F58]/90 transition-colors"
            @click="showCreateModal = true"
          >
            <span class="material-symbols-outlined text-base leading-none">add</span>
            <span class="hidden sm:inline">Nuevo rol</span>
          </button>
        </HasPermission>
        <CreateRoleModal
          :visible="showCreateModal"
          :company-selected="selectedCompany ?? (user?.internal ? undefined : { id: user?.companyId } as any)"
          @action-modal="showCreateModal = false; refetch()"
        />
      </div>

      <!-- Desktop: side by side | Mobile: stacked -->
      <div class="md:flex md:min-h-[560px]">

        <!-- Role list -->
        <div
          class="border-gray-100 overflow-y-auto transition-all duration-200"
          :class="[
            'md:w-[30%]',
            'md:flex-shrink-0 md:border-r',
            mobileView === 'abilities' ? 'hidden md:block' : 'block',
          ]"
        >
          <!-- Skeleton -->
          <div v-if="isLoading" class="p-3 space-y-2">
            <div v-for="i in 4" :key="i" class="h-16 rounded-xl bg-gray-100 animate-pulse" />
          </div>

          <!-- Empty: no company selected (internal) -->
          <div
            v-else-if="user?.internal && !selectedCompany"
            class="flex flex-col items-center justify-center py-16 text-gray-400"
          >
            <span class="material-symbols-outlined text-4xl text-gray-200 mb-3">business</span>
            <p class="text-sm font-medium text-gray-400">Selecciona una empresa</p>
          </div>

          <!-- Empty: no roles -->
          <div v-else-if="!data?.length" class="flex flex-col items-center justify-center py-16 text-gray-400">
            <div class="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
              <span class="material-symbols-outlined text-2xl">policy</span>
            </div>
            <p class="text-sm font-medium text-gray-500">Sin roles</p>
            <p class="text-xs text-gray-400 mt-1">Crea el primer rol para comenzar</p>
          </div>

          <!-- List -->
          <div v-else class="p-3 space-y-1.5">
            <button
              v-for="role in data"
              :key="role.id"
              type="button"
              class="w-full group flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all duration-150"
              :class="selectedRole?.id === role.id
                ? 'bg-[#021F58] shadow-md shadow-[#021F58]/20'
                : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'"
              @click="selectRole(role)"
            >
              <div
                class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold transition-colors"
                :class="selectedRole?.id === role.id ? 'bg-white/20 text-white' : roleColor(role.name)"
              >
                {{ role.name.charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold truncate transition-colors" :class="selectedRole?.id === role.id ? 'text-white' : 'text-gray-900'">
                  {{ role.name }}
                </p>
                <p class="text-xs truncate transition-colors" :class="selectedRole?.id === role.id ? 'text-white/60' : 'text-gray-400'">
                  {{ role.usersCount ?? 0 }} {{ (role.usersCount ?? 0) === 1 ? 'usuario' : 'usuarios' }}
                </p>
              </div>
              <svg
                class="w-4 h-4 flex-shrink-0 transition-all"
                :class="selectedRole?.id === role.id ? 'text-white/70 rotate-90' : 'text-gray-300 group-hover:text-gray-400'"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Abilities panel -->
        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          enter-from-class="opacity-0 translate-x-3"
          enter-to-class="opacity-100 translate-x-0"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
          mode="out-in"
        >
          <div
            v-if="selectedRole"
            :key="selectedRole.id"
            class="flex-1 flex flex-col min-w-0 overflow-hidden min-h-[500px] md:min-h-0"
            :class="mobileView === 'list' ? 'hidden md:flex' : 'flex'"
          >
            <CompanyRoleAbilitiesPanel ref="abilitiesPanelRef" :role="selectedRole" />
          </div>

          <!-- Placeholder desktop -->
          <div
            v-else-if="data?.length"
            class="hidden md:flex flex-1 flex-col items-center justify-center gap-8 bg-gray-50/60 px-12"
          >
            <div class="relative z-10 flex items-center justify-center w-52 h-52">
              <div class="absolute inset-0 rounded-full bg-gray-100/80" />
              <div class="absolute w-40 h-40 rounded-full bg-gray-100" />
              <div class="relative z-10 w-28 h-28 rounded-3xl bg-white border border-gray-100 shadow-lg flex items-center justify-center">
                <span class="material-symbols-outlined text-[56px] text-gray-300">shield_person</span>
              </div>
            </div>
            <div class="relative z-10 text-center space-y-2">
              <p class="text-lg font-semibold text-gray-500">Selecciona un rol</p>
              <p class="text-sm text-gray-400 leading-relaxed">Elige un rol de la lista para ver<br>y gestionar sus permisos</p>
            </div>
            <div class="relative z-10 flex flex-col gap-3">
              <div
                v-for="hint in ['Ver permisos asignados', 'Editar capacidades', 'Ver miembros del rol']"
                :key="hint"
                class="flex items-center gap-3 text-sm text-gray-400"
              >
                <span class="w-5 h-5 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                  <span class="w-1.5 h-1.5 rounded-full bg-gray-300" />
                </span>
                {{ hint }}
              </div>
            </div>
          </div>
        </Transition>

      </div>
    </div>

  </div>

  <!-- Guardado obligatorio: no se puede cambiar de rol/empresa con permisos sin guardar -->
  <UnsavedRoleChangesModal
    :model-value="showUnsavedModal"
    :role-name="selectedRole?.name"
    :saving="savingBeforeSwitch"
    :save-failed="saveFailed"
    @keep-editing="keepEditing"
    @save-and-continue="saveAndContinue"
    @discard-and-continue="discardAndContinue"
  />
</template>
